import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { EChartsOption } from 'echarts';
import { AlTableColumn } from '../features/table/table.component';
import { SegmentOption } from '../segmented-control/segmented-control.component';
import { RadarAxisInput, RadarTexts } from '../charts/basic/competency-radar/competency-radar.options';
import {
  buildView,
  CompetencyCourse,
  CompetencyLensKey,
  CompetencyProfile,
} from '../competency-profile/competency-profile.model';
import { moodleCourseProgressText } from '../../helpers/moodle-progress';
import {
  factValue,
  filterGroups,
  I18N,
  knowledgeStep,
  KNOWLEDGE_CODES,
  MasteryRow,
  masteryRows,
  MemoryView,
  ObservationBar,
  observationBars,
  ObservationType,
  preferenceLabel,
  ringDash,
  shortDate,
  SoloBar,
  soloBar,
  sparkPoints,
  TIER_COLOR,
  TIERS,
  TopicBar,
  topicBars,
  TYPE_COLOR,
  TYPE_ORDER,
} from './learner-memory.model';

type Lens = CompetencyLensKey | 'mastery';

const FACT_ICON: Record<string, string> = {
  name: 'person-outline', job: 'briefcase-outline', knowledge_level: 'award-outline',
  interests: 'heart-outline', personality_notes: 'bulb-outline',
  preferred_pace: 'flash-outline', preferred_format: 'book-open-outline', language_pref: 'globe-outline',
  timezone: 'clock-outline', favorite_color: 'color-palette-outline', hobby: 'heart-outline',
};

/** At most this many dots per raster; more answers are summed up in the text. */
const MAX_DOTS = 60;

/**
 * Lernerprofil for the lecturer (Dozent › Teilnehmer), learner-profile v2 in
 * its visual form (PRD mastery-objectives M1): key figures as ring, scale and
 * streak; strengths and gaps as a radar over the competency profile's lenses
 * or the mastery tiers per area; exercise topics as bars; the mastery trend;
 * learning behaviour as dot rasters; what the agent noticed as stacked bars
 * per subject (items on click); activity; profile chips and the summary.
 * Reads `view` (al-tutor `learner_memory_ops.memory_view`) and the competency
 * profile of the same learner. Every array is built once per input or filter
 * change — nothing hands a chart or app-table a fresh array (Regel 18).
 */
@Component({
  selector: 'app-learner-memory',
  templateUrl: './learner-memory.component.html',
  styleUrl: './learner-memory.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LearnerMemoryComponent implements OnChanges {
  @Input() data: any = null;
  @Input() loading = false;
  /** The same learner's competency profile (lecturer cut); null while unknown. */
  @Input() competency: CompetencyProfile | null = null;

  readonly I18N = I18N;
  readonly TIERS = TIERS;
  readonly TIER_COLOR = TIER_COLOR;
  readonly TYPE_COLOR = TYPE_COLOR;
  readonly TYPE_ORDER = TYPE_ORDER;
  readonly ring = { r: 26, size: 64 };
  readonly scaleSteps = KNOWLEDGE_CODES;

  view: MemoryView | null = null;
  updated = '';
  // key figures
  exercisePct = 0;
  exerciseDash = { array: '0', offset: 0 };
  knowledge = -1;
  knowledgeLabel = '';
  streakDots: boolean[] = [];
  spark = '';
  // strengths & gaps
  course: CompetencyCourse | null = null;
  lens: Lens = 'areas';
  lensOptions: SegmentOption[] = [];
  radarAxes: RadarAxisInput[] = [];
  radarTexts: RadarTexts | null = null;
  radarAria = '';
  mastery: MasteryRow[] = [];
  topics: TopicBar[] = [];
  // trend & behaviour
  trendOptions: EChartsOption | null = null;
  behaviour: { key: string; label: string; value: string; unit: string; dots: string[]; note: string }[] = [];
  /** Answer depth (SOLO, M5) as one stacked bar — null without data. */
  solo: SoloBar | null = null;
  // observations
  typeFilter: ObservationType | 'all' = 'all';
  typeOptions: SegmentOption[] = [];
  query = '';
  bars: ObservationBar[] = [];
  open = new Set<string>();
  // activity
  chartLabels: string[] = [];
  chartSeries: { name: string; data: number[]; stack: string }[] = [];
  hasActivity = false;
  // profile
  chips: { icon: string; label: string; value: string }[] = [];
  summaryMode: 'tutor' | 'learner' = 'tutor';
  summaryOptions: SegmentOption[] = [];
  moodleColumns: AlTableColumn[] = [];
  moodleRows: any[] = [];

  constructor(private translate: TranslateService) {}

  private t = (key: string, params?: Record<string, unknown>) => this.translate.instant(key, params);
  private k = (key: string, params?: Record<string, unknown>) => this.t(`${I18N}.${key}`, params);

  get lang(): string {
    return this.translate.currentLang || 'de';
  }

  get summaryText(): string | null | undefined {
    return this.summaryMode === 'learner' ? this.view?.summary.learner_text : this.view?.summary.text;
  }

  ngOnChanges(): void {
    this.view = (this.data?.view as MemoryView) || null;
    const v = this.view;
    this.buildCompetency();
    if (!v) {
      this.moodleRows = [];
      return;
    }
    const f = v.figures;
    this.updated = shortDate(v.summary.updated_at, this.lang);
    this.exercisePct = f.exercises ? f.exercises_correct / f.exercises : 0;
    this.exerciseDash = ringDash(this.exercisePct, this.ring.r);
    this.knowledge = knowledgeStep(v.facts);
    this.knowledgeLabel = this.knowledge >= 0 ? this.k(`level.${KNOWLEDGE_CODES[this.knowledge]}`) : '';
    this.streakDots = Array.from({ length: 7 }, (_, i) => i < Math.min(7, f.streak_days || 0));
    const weeks = v.observations.weekly || [];
    this.spark = sparkPoints(weeks.map((w) => TYPE_ORDER.reduce((s, type) => s + (w[type] || 0), 0)), 140, 30);

    this.topics = topicBars(v.exercise_topics || [], this.t);

    this.chartLabels = weeks.map((w) => shortDate(w.week_start, this.lang).slice(0, 6));
    this.chartSeries = TYPE_ORDER.map((type) => ({
      name: this.k(`type.${type}`), data: weeks.map((w) => w[type] || 0), stack: 'memory',
    }));
    this.hasActivity = weeks.some((w) => TYPE_ORDER.some((type) => w[type] > 0));

    this.typeOptions = [
      { value: 'all', label: this.k('filterAll', { count: v.observations.total }) },
      ...TYPE_ORDER.filter((type) => v.observations.by_type[type]).map((type) => ({
        value: type, label: `${this.k(`type.${type}`)} (${v.observations.by_type[type]})`,
      })),
    ];
    this.typeFilter = 'all';
    this.query = '';
    this.open = new Set<string>();
    this.refilter();

    this.chips = [
      ...v.facts.filter((x) => x.field !== 'knowledge_level' || this.knowledge < 0).map((x) => ({
        icon: FACT_ICON[x.field] || 'bulb-outline', label: this.k(`fact.${x.field}`), value: factValue(x, this.t),
      })),
      ...v.preferences.map((p) => ({
        icon: FACT_ICON[p.key] || 'bulb-outline', label: preferenceLabel(p, this.t), value: p.value,
      })),
    ];
    this.summaryOptions = [
      { value: 'tutor', label: this.k('summaryTutor') },
      { value: 'learner', label: this.k('summaryLearner'), disabled: !v.summary.learner_text },
    ];
    this.summaryMode = 'tutor';
    this.buildMoodle();
  }

  setLens(lens: string): void {
    this.lens = (['areas', 'bloom', 'dqr', 'goals', 'mastery'].includes(lens) ? lens : 'areas') as Lens;
    this.buildLens();
  }

  setSummaryMode(mode: string): void {
    this.summaryMode = mode === 'learner' ? 'learner' : 'tutor';
  }

  setType(type: string): void {
    this.typeFilter = type as ObservationType | 'all';
    this.refilter();
  }

  setQuery(q: string): void {
    this.query = q || '';
    this.refilter();
  }

  toggle(key: string): void {
    const next = new Set(this.open);
    next.has(key) ? next.delete(key) : next.add(key);
    this.open = next;
  }

  trackKey(_i: number, x: { key: string }): string {
    return x.key;
  }

  private refilter(): void {
    const v = this.view;
    this.bars = v
      ? observationBars(filterGroups(v.observations.groups, this.typeFilter, this.query, this.t, this.lang), v.observations.groups)
      : [];
  }

  // ─── Competency: radar, mastery bars, trend, behaviour ─────────────────────

  private buildCompetency(): void {
    const p = this.competency;
    this.course = p?.courses?.find((c) => c.course_id === p.default_course_id) || p?.courses?.[0] || null;
    this.lensOptions = [
      { value: 'areas', label: this.k('lens.areas') },
      { value: 'bloom', label: this.k('lens.bloom') },
      { value: 'dqr', label: this.k('lens.dqr') },
      { value: 'mastery', label: this.k('lens.mastery') },
      ...(this.course?.lenses.goals ? [{ value: 'goals', label: this.t('components.competency.lenses.goals') }] : []),
    ];
    if (this.lens === 'goals' && !this.course?.lenses.goals) this.lens = 'areas';
    this.radarTexts = {
      level: this.t('components.competency.seriesLevel'),
      coverage: this.t('components.competency.seriesCoverage'),
      rings: [this.k('tier.learning'), this.k('tier.proficient'), this.k('tier.mastered')],
    };
    this.buildLens();
    this.buildTrend();
    this.buildBehaviour();
  }

  private buildLens(): void {
    const p = this.competency;
    const c = this.course;
    this.radarAxes = [];
    this.mastery = [];
    if (!p || !c) return;
    if (this.lens === 'mastery') {
      this.mastery = masteryRows(
        c.lenses.areas.axes.map((a) => ({ key: a.key, label: a.label, total: a.total, tiers: a.tiers })), this.t,
      );
      return;
    }
    const view = buildView(p, c.course_id, this.lens, this.t, this.lang);
    if (!view) return;
    this.radarAria = view.aria;
    this.radarAxes = view.axes.length >= 3
      ? view.axes.map((a) => ({
          key: a.key, name: a.label, level: a.level, coverage: a.coverage,
          muted: a.state === 'not_captured' || a.state === 'open', levelLabel: a.levelLabel,
        }))
      : [];
    // Fewer than three axes are no shape: the mastery bars say it better.
    if (!this.radarAxes.length) {
      const lens = c.lenses[this.lens as CompetencyLensKey] || c.lenses.areas;
      this.mastery = masteryRows(lens.axes.map((a) => ({
        key: a.key, label: view.axes.find((x) => x.key === a.key)?.label || a.label, total: a.total, tiers: a.tiers,
      })), this.t);
    }
  }

  private buildTrend(): void {
    const trend: { week_start: string; tiers: Record<string, number> }[] = (this.course as any)?.trend || [];
    if (!trend.length || !trend.some((w) => TIERS.some((tier) => tier !== 'new' && w.tiers?.[tier]))) {
      this.trendOptions = null;
      return;
    }
    const labels = trend.map((w) => shortDate(w.week_start, this.lang).slice(0, 6));
    this.trendOptions = {
      animation: !(typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches),
      grid: { left: 36, right: 12, top: 12, bottom: 28 },
      tooltip: { trigger: 'axis', confine: true },
      xAxis: { type: 'category', boundaryGap: false, data: labels, axisLabel: { color: '#526581', fontSize: 11 } },
      yAxis: { type: 'value', minInterval: 1, axisLabel: { color: '#526581', fontSize: 11 },
               splitLine: { lineStyle: { color: '#e3e9f2', type: 'dashed' } } },
      series: [...TIERS].reverse().map((tier) => ({
        name: this.k(`tier.${tier}`),
        type: 'line' as const,
        stack: 'tiers',
        symbol: 'none',
        smooth: 0.25,
        lineStyle: { width: 0 },
        areaStyle: { color: TIER_COLOR[tier], opacity: 1 },
        itemStyle: { color: TIER_COLOR[tier] },
        data: trend.map((w) => w.tiers?.[tier] || 0),
      })),
    };
  }

  private buildBehaviour(): void {
    const b = this.course?.behaviour;
    const due = this.course?.summary?.due || 0;
    this.behaviour = [];
    this.solo = soloBar(b?.solo, this.t);
    const dots = (n: number, of: number) =>
      Array.from({ length: Math.min(of, MAX_DOTS) }, (_, i) => (i < Math.min(n, MAX_DOTS) ? 'on' : 'off'));
    if (b?.answers) {
      this.behaviour.push({
        key: 'unassisted', label: this.k('behaviour.unassisted'), value: String(b.correct_unassisted),
        unit: this.k('behaviour.ofAnswers', { total: b.answers }), dots: dots(b.correct_unassisted, b.answers),
        note: b.answers > MAX_DOTS ? this.k('behaviour.capped', { count: MAX_DOTS }) : this.k('behaviour.perAnswer'),
      });
    }
    if (b?.sure) {
      this.behaviour.push({
        key: 'sure', label: this.k('behaviour.sure'), value: String(b.sure_correct),
        unit: this.k('behaviour.ofSure', { total: b.sure }), dots: dots(b.sure_correct, b.sure),
        note: b.calibration_ready ? this.k('behaviour.calibration') : this.k('behaviour.fewSure'),
      });
    }
    if (this.course) {
      this.behaviour.push({
        key: 'due', label: this.k('behaviour.due'), value: String(due),
        unit: this.k('behaviour.objectives'), dots: dots(due, Math.max(due, 12)),
        note: this.k('behaviour.dueNote'),
      });
    }
  }

  // ─── Moodle context (unchanged) ───────────────────────────────────────────

  private buildMoodle(): void {
    const profile = this.data?.profile || {};
    const moodle = profile.moodle || profile.learning_history?.moodle || this.data?.learning_progress?.learning_history?.moodle;
    let courses = moodle?.courses || moodle?.enrollments || [];
    if (courses && !Array.isArray(courses) && typeof courses === 'object') courses = Object.values(courses);
    this.moodleColumns = [
      { key: 'name', title: this.k('moodle.course') },
      { key: 'category', title: this.k('moodle.category'), kind: 'text' },
      { key: 'status', title: this.k('moodle.status') },
      { key: 'progress', title: this.k('moodle.progress') },
      { key: 'grade', title: this.k('moodle.grade') },
    ];
    this.moodleRows = (Array.isArray(courses) ? courses : []).map((c: any, i: number) => ({
      id: String(c?.course_id || c?.id || i),
      name: c?.course_name || c?.fullname || c?.shortname || c?.course_shortname || '—',
      category: String(c?.category_name || c?.category || '') || '—',
      status: this.moodleStatus(c),
      progress: moodleCourseProgressText(c, this.k('moodle.completed')),
      grade: c?.grade_formatted
        || (c?.grades || []).map((g: any) => g.grade_formatted || g.raw_grade).filter(Boolean).join(', ')
        || '—',
    }));
  }

  private moodleStatus(c: any): string {
    if (c?.completed !== undefined && c?.completed !== null) {
      return c.completed === true || c.completed === 'true' ? this.k('moodle.completed') : this.k('moodle.open');
    }
    const role = String(c?.role || c?.status || '').toLowerCase();
    if (!role) return '—';
    const key = `${I18N}.moodle.role.${role}`;
    const label = this.t(key);
    return label !== key ? label : role;
  }
}
